import { Globals } from "../Globals";
import { kROUser } from "../../constants";
import { enumWidgetType } from "./widgetTypes";

// Module scope: create a no-op pendo stub that warns if called before Load().
// Prevents crashes if anything references window.pendo early.
if (!window.pendo) {
  const pendoGuard = {};
  const pendoMethods = ["initialize", "identify", "updateOptions", "pageLoad", "track"];
  for (const method of pendoMethods) {
    pendoGuard[method] = () => {
      console.warn(`[QWidgetMgr] pendo.${method} called before widget initialization`);
    };
  }
  pendoGuard._q = [];
  window.pendo = pendoGuard;
}

const PENDO_API_KEY = "0224da5e-0f86-4422-74cc-6d91053e5534";

// Read-only users are not tracked. An account may opt a narrow cohort in via the
// pendoReadOnlyTracking package/account feature (see ServerSide app_globals.py
// enumFeaturesList); absent that feature, no RO user is tracked. Driving this from the
// account's features means ops can retune the cohort per edition or per account without a
// frontend deploy, instead of it being hardcoded in a brand file.
function MatchesReadOnlyTrackingCohort(wsMgr, userinfo) {
  const spec = Globals._accountInfo.GetAccessibleFeature("pendoReadOnlyTracking");
  if (!spec) return false;
  if (wsMgr.fAdminLogin) return false;

  const allowedStatuses = spec.statuses ?? (spec.status == null ? [] : [spec.status]);
  const accountStatus = Globals._accountInfo.GetAccountStatus();
  if (
    !Array.isArray(allowedStatuses) ||
    !accountStatus ||
    !allowedStatuses.includes(accountStatus)
  ) {
    return false;
  }

  const userName = userinfo.GetUserName();
  if (!userName) return false;

  // usernames are not lowercased server-side (qusermgr.py:1063), so compare case-insensitively.
  const lowerUserName = userName.toLowerCase();
  // Usernames aren't guaranteed to be email-shaped (SSO/SCIM can provision a bare identifier
  // with no "@"), so domain fragments are anchored (exact match or subdomain of the fragment)
  // while other fragments stay a plain substring match.
  const atIndex = lowerUserName.lastIndexOf("@");
  const userDomain = atIndex === -1 ? null : lowerUserName.slice(atIndex + 1);

  const exclusions = spec.excludeUserNameSubstrings ?? [];
  if (!Array.isArray(exclusions) || !exclusions.every(fragment => typeof fragment === "string")) {
    return false;
  }
  return !exclusions.some(fragment => {
    const lowerFragment = fragment.toLowerCase();
    if (lowerFragment.startsWith("@")) {
      const excludedDomain = lowerFragment.slice(1);
      return userDomain === excludedDomain || (userDomain?.endsWith(`.${excludedDomain}`) ?? false);
    }
    // Known imprecise: There's no established naming convention for the account(s)
    // this is meant to catch, so it isn't anchored further
    return lowerUserName.includes(lowerFragment);
  });
}

export const PendoWidget = {
  type: enumWidgetType.pendo,

  Load(widgetConfig, context) {
    const { wsMgr, orgDict } = context;
    const userinfo = wsMgr.GetUserInfo();
    const orginfo = wsMgr.fOrgInfo;

    const hasPendingLogin = !!sessionStorage.getItem("_pendoLogin");
    sessionStorage.removeItem("_pendoLogin");

    if (!orginfo) {
      userinfo.fShouldPendo = false;
      return false;
    }
    if (Globals._accountInfo.ReportToWebDriver()) {
      userinfo.fShouldPendo = false;
      return false;
    }
    if (userinfo.GetUserRole() === kROUser && !MatchesReadOnlyTrackingCohort(wsMgr, userinfo)) {
      userinfo.fShouldPendo = false;
      return false;
    }

    // Bootstrap the real Pendo SDK (moved from app.html IIFE)
    // Clear the guard stub so the IIFE's o[m] = o[m] || ... installs real queuing functions
    const apiKey = widgetConfig.config?.apiKey ?? PENDO_API_KEY;
    delete window.pendo;
    (function (p, e, n, d, o) {
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      const v = ["initialize", "identify", "updateOptions", "pageLoad", "track"];
      for (let w = 0, x = v.length; w < x; ++w)
        (function (m) {
          o[m] =
            o[m] ||
            function () {
              o._q[m === v[0] ? "unshift" : "push"]([m].concat([].slice.call(arguments, 0)));
            };
        })(v[w]);
      const y = e.createElement(n);
      y.async = !0;
      y.src = "https://cdn.pendo.io/agent/static/" + apiKey + "/pendo.js";
      const z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
    })(window, document, "script", "pendo");

    userinfo.fShouldPendo = true;
    wsMgr.GetUsageTracker().Enable();

    const accessMgr = wsMgr.GetAccessGroupMgr();
    window.pendo.initialize({
      visitor: {
        id: userinfo.GetUserName(),
        email: userinfo.GetUserEmail(),
        role: userinfo.GetUserRole(),
        group: userinfo.GetUserGroupID(),
        supportLogin: wsMgr.fAdminLogin,
        platform: "OrgChart 2.0",
        mfa: userinfo.GetMFA() != null,
        language: userinfo.fLanguage
      },
      account: {
        id: orginfo.fAccountName,
        name: orginfo.GetOrgName(),
        monthly_value: null,
        planLevel: orginfo.GetBoxCount(),
        creationDate: orginfo.fAccountDetails.GetStartDate(),
        expireDate: orginfo.GetExpireDate(),
        server: window.location.host,
        status: Globals._accountInfo.GetAccountStatus(),
        edition: Globals._accountInfo.GetOC5Edition(),
        seats: orginfo.GetSeatCount(),
        allowedBoxLimit: orginfo.GetAllowedBoxCount(),
        currentBoxCount: orginfo.fCurBoxUsage,
        numBaseCharts: orginfo.GetMasterChartList().length,
        numRLSProfiles: Object.keys(accessMgr.fRowlevelProfiles ?? {}).length,
        numReferenceTables: orgDict["referenceTables"]?.length ?? 0,
        numBLSProfiles: Object.keys(accessMgr.fBranchLevelProfiles ?? {}).length,
        doesAutoUserAssign: accessMgr.fUserAssignmentConfig?.enabled === true,
        sso: orgDict.ssoEnabled === true,
        ssoProvider: orgDict.ssoEnabled ? orgDict.ssoProvider : null,
        numAppend: orginfo.GetMasterChartList().reduce((accumulator, x) => {
          return accumulator + (x.append?.length ?? 0);
        }, 0),
        numMerge: orginfo.GetMasterChartList().reduce((accumulator, x) => {
          return accumulator + (x.merge?.length ?? 0);
        }, 0),
        dataSources: orginfo.fDataSources,
        appendSources: orginfo.fAppendSources,
        mergeSources: orginfo.fMergeSources
      }
    });

    if (hasPendingLogin) {
      wsMgr.GetUsageTracker().ReportEvent("global.login.completed");
    }

    return true;
  },

  Activate(context) {
    const { wsMgr } = context;
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("resource_center.help_menu.opened");
  },

  Unload() {
    try {
      window.pendo?.teardown?.();
    } catch (e) {
      console.warn("[PendoWidget] pendo.teardown failed", e);
    }
  }
};
